import { useCallback, useEffect, useRef, useState } from "react";

/**
 * PANEL GÜNCELLEME DENETİMİ
 *
 * Sorun: CRA çıktısı hash'li asset'ler + tarayıcının `index.html` önbelleği
 * yüzünden yeni sürüm yayınlansa bile açık duran sekmeler eski paneli
 * kullanmaya devam ediyordu; kullanıcı sert yenileme (Ctrl+F5) yapmadan
 * güncellemeyi almıyordu.
 *
 * Çözüm: build'in kendi parmak izi olan `asset-manifest.json` periyodik
 * olarak `no-store` ile çekilir. İçindeki `main.js` yolu, sayfanın AÇILIŞTA
 * yüklediği main.js yolundan farklıysa yeni bir dağıtım yapılmış demektir.
 * Sunucu tarafında hiçbir uç gerekmez — manifest zaten statik olarak
 * servis ediliyor.
 *
 * Sürüm notları `public/release-notes.json` dosyasından okunur (en yeni
 * sürüm en üstte). Kullanıcının "Tamam" dediği son sürüm localStorage'da
 * tutulur; bir daha o ve daha eski sürümlerin notları gösterilmez.
 *
 * YENİ SÜRÜM ÇIKARIRKEN: `public/release-notes.json` dosyasının başına yeni
 * bir kayıt ekle (version = package.json sürümü). Eklenmezse güncelleme
 * yine tespit edilir, modal notsuz "yeni sürüm var" olarak çıkar.
 */

const STORAGE_KEY = "lastSeenPanelVersion";
// 5 dakika: deploy'u makul sürede yakalar, gereksiz istek yaratmaz.
const POLL_INTERVAL_MS = 5 * 60 * 1000;

const publicPath = (file) => {
  const base = process.env.PUBLIC_URL || "";
  return `${base}/${file}`.replace(/([^:]\/)\/+/g, "$1");
};

/** "0.61.0" > "0.60.2" karşılaştırması (eksik hane 0 sayılır). */
export const isNewerVersion = (a, b) => {
  if (!a) return false;
  if (!b) return true;
  const pa = String(a).split(".").map((n) => parseInt(n, 10) || 0);
  const pb = String(b).split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const x = pa[i] || 0;
    const y = pb[i] || 0;
    if (x !== y) return x > y;
  }
  return false;
};

/** Sayfanın şu an çalıştırdığı main.js — build parmak izi. */
const getRunningBuildId = () => {
  const el = document.querySelector('script[src*="/static/js/main."]');
  if (!el) return null;
  const src = el.getAttribute("src") || "";
  const match = src.match(/main\.[^./]+\.js/);
  return match ? match[0] : src;
};

const fetchNoStore = async (url) => {
  const res = await fetch(`${url}?t=${Date.now()}`, { cache: "no-store" });
  if (!res.ok) throw new Error(`${url} okunamadı (${res.status})`);
  return res.json();
};

export default function useAppUpdateCheck() {
  // Açılışta yüklenen build — poll sonuçları hep bununla karşılaştırılır.
  const runningBuildIdRef = useRef(getRunningBuildId());
  const [updateAvailable, setUpdateAvailable] = useState(false);
  const [releases, setReleases] = useState([]);
  const [lastSeen, setLastSeen] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || null;
    } catch (_) {
      return null;
    }
  });

  // Sürüm notlarını bir kez oku (açılıştaki dağıtımın notları).
  useEffect(() => {
    let cancelled = false;
    fetchNoStore(publicPath("release-notes.json"))
      .then((data) => {
        if (cancelled) return;
        const list = Array.isArray(data?.releases) ? data.releases : [];
        // En yeni üstte olacak şekilde sırala — dosyadaki sıraya güvenme.
        list.sort((a, b) => (isNewerVersion(a.version, b.version) ? -1 : 1));
        setReleases(list);
      })
      .catch(() => {
        // Not dosyası yoksa/bozuksa güncelleme denetimi yine çalışır.
        if (!cancelled) setReleases([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // Yeni dağıtım denetimi — periyodik + sekmeye geri dönüldüğünde.
  useEffect(() => {
    let cancelled = false;
    const check = async () => {
      if (cancelled || document.visibilityState === "hidden") return;
      try {
        const manifest = await fetchNoStore(publicPath("asset-manifest.json"));
        const mainJs = manifest?.files?.["main.js"] || "";
        const match = String(mainJs).match(/main\.[^./]+\.js/);
        const deployedBuildId = match ? match[0] : null;
        if (!deployedBuildId || !runningBuildIdRef.current) return;
        if (deployedBuildId !== runningBuildIdRef.current) {
          setUpdateAvailable(true);
        }
      } catch (_) {
        // Ağ hatası sessiz geçilir; bir sonraki denemede tekrar bakılır.
      }
    };

    const timer = setInterval(check, POLL_INTERVAL_MS);
    const onFocus = () => check();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    check();

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  const currentVersion = releases[0]?.version || null;

  // Gösterilecek notlar: kullanıcının onayladığı sürümden YENİ olanlar.
  const pendingReleases = releases.filter((r) =>
    isNewerVersion(r.version, lastSeen),
  );

  // HİÇ NOT OKUMAMIŞ KULLANICI (localStorage'da kayıt yok): sistemi ilk kez
  // gören herkes bu durumda — mevcut tüm kullanıcılar dahil. Sessiz geçilirse
  // ilk sürümün notlarını kimse görmez. Bu yüzden YALNIZ EN GÜNCEL sürümün
  // notu gösterilir; eski sürümlerin geçmişi yığılmaz.
  const isFirstRun = !lastSeen;

  const acknowledge = useCallback(
    ({ reload = false } = {}) => {
      try {
        if (currentVersion) localStorage.setItem(STORAGE_KEY, currentVersion);
      } catch (_) {
        // localStorage kapalıysa (gizli sekme) sessiz geç.
      }
      setLastSeen(currentVersion);
      setUpdateAvailable(false);
      if (reload) window.location.reload();
    },
    [currentVersion],
  );

  return {
    // Yeni dağıtım var: modal ZORUNLU (kapatılamaz), tek çıkış yenileme.
    updateAvailable,
    // Gösterilecek notlar:
    //  - hiç not okumamış kullanıcı → yalnız en güncel sürüm,
    //  - okumuş kullanıcı → onayladığı sürümden sonraki TÜM sürümler.
    // Her iki durumda da "Tamam" güncel sürümü işaretler; kapanış
    // `acknowledge` ile olur, sessiz işaretleme YOK.
    pendingReleases:
      isFirstRun && releases.length > 0 ? [releases[0]] : pendingReleases,
    currentVersion,
    acknowledge,
  };
}
